"use client";

import { useRef } from "react";
import { Button, CODE, GradText, SectionHead, SectionShell, Terminal, useBandRowAlign, useWholeRowBlocks } from "./kit";
import { gsap, NOT_REDUCED, playOnEnter, useEnterStagger, useGSAP } from "./kit/motion";
import { routePath } from "@/lib/routes";

const REGION = "block border-l-2 border-l-brand-600/25 pl-3 -ml-3";

const CALLOUTS: {
  for: "routing" | "url" | "expiry" | "contract";
  kicker: string;
  body: React.ReactNode;
}[] = [
  {
    for: "routing",
    kicker: "url / routing",
    body: <>Every rule in order, plus the fallback — not just one URL.</>,
  },
  {
    for: "url",
    kicker: "slug / short_url",
    body: "The public URL on your domain, ready to share.",
  },
  {
    for: "expiry",
    kicker: "expires_at",
    body: "When it stops answering — a 410 after that.",
  },
  {
    for: "contract",
    kicker: "id / updated_at",
    body: "A stable, versioned /v1 shape, published as OpenAPI.",
  },
];

/**
 * The link record an agent gets back from MCP get_link (the same shape as
 * the public API's GET /v1/links/{id}), annotated: four regions of the response get a left-border grouping in the
 * terminal, matched to four callouts on the right by connector lines.
 *
 * Region borders are STATIC (`border-l-brand-600/25`) per the brief's
 * preference — only the connectors animate in. Entrance-only motion: the
 * callouts rise with `useEnterStagger`, the connectors scale in from the
 * left once the block enters the viewport. Under `prefers-reduced-motion`
 * neither `gsap.matchMedia` branch runs, so the connectors are never
 * `gsap.set` to `scaleX: 0` in the first place — they render at their
 * natural (fully visible) scale.
 */
export default function PayloadAnatomySection() {
  const rootRef = useRef<HTMLDivElement>(null);

  // Whole-row block sizing (default +1 breathing row — this block is not
  // snug) + row alignment, the standard pair every grid-mode section uses
  // so its clearing's edges land exactly on grid lines and its content
  // centers within the whole rows (previously missing here, which left the
  // block's edges off the row lines and the clearing snapping outward
  // asymmetrically above/below the content).
  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);

  // Callouts rise in sequence as the block enters.
  useEnterStagger(rootRef, "[data-payload-callout]", { y: 20, stagger: 0.12 });

  // Connectors draw in (scaleX 0 → 1) once on enter, matching the JSON
  // regions they point at. Skipped entirely under reduced motion.
  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(NOT_REDUCED, () => {
        const el = rootRef.current;
        const connectors = el?.querySelectorAll("[data-payload-connector]");
        if (!el || !connectors || !connectors.length) return;
        gsap.set(connectors, { scaleX: 0 });
        const tween = gsap.to(connectors, {
          scaleX: 1,
          duration: 0.4,
          stagger: 0.12,
          ease: "power2.out",
          delay: 0.3,
          paused: true,
        });
        return playOnEnter(tween, el);
      });
    },
    { scope: rootRef },
  );

  return (
    <SectionShell id="ref-payload">
      <SectionHead
        eyebrow="The payload"
        title={
          <>
            Everything an agent needs.
            <br />
            <GradText>One response.</GradText>
          </>
        }
        lead="One JSON shape for every link your agent creates over MCP or the API: destination, routing rules (Premium), expiry and ids — documented and versioned."
      />

      <div
        ref={rootRef}
        data-grid-block
        className="grid items-center gap-12 md:grid-cols-[1.15fr_1fr]"
      >
        <div className="min-w-0">
          <Terminal path="get_link · GET /v1/links/{id}" minHeight={300}>
            <pre className="m-0 whitespace-pre-wrap font-[inherit] [overflow-wrap:anywhere]">
              {/* `data-payload-region` spans are `display:block`, so each one
                  already forces its own line break before/after itself (an
                  anonymous-block boundary, same as a <div> would) — no `\n`
                  is placed directly against a region's open/close tag, or
                  the block break plus that literal newline would render as
                  a stray blank line. Indentation for a region's first line
                  lives inside the region (e.g. the leading "  "), never
                  carried over from the preceding plain-text run. */}
              {"{\n  "}
              <span className={CODE.key}>&quot;id&quot;</span>:{" "}
              <span className={CODE.string}>&quot;lnk_8h2f0qZ3Tk1mR4vXw9aBcQ&quot;</span>,
              <span data-payload-region="routing" className={REGION}>
                {"  "}
                <span className={CODE.key}>&quot;url&quot;</span>:{" "}
                <span className={CODE.string}>&quot;https://myshop.com&quot;</span>,{"\n  "}
                <span className={CODE.key}>&quot;routing&quot;</span>: {"[\n    { "}
                <span className={CODE.key}>&quot;if&quot;</span>: {"{ "}<span className={CODE.key}>&quot;device&quot;</span>: <span className={CODE.string}>&quot;ios&quot;</span>{" }, "}
                <span className={CODE.key}>&quot;to&quot;</span>: <span className={CODE.string}>&quot;https://apps.apple.com/app/id123&quot;</span>{" },\n    { "}
                <span className={CODE.key}>&quot;if&quot;</span>: {"{ "}<span className={CODE.key}>&quot;device&quot;</span>: <span className={CODE.string}>&quot;android&quot;</span>{" }, "}
                <span className={CODE.key}>&quot;to&quot;</span>: <span className={CODE.string}>&quot;https://play.google.com/store/id123&quot;</span>{" }"}
              </span>
              {"  ],"}
              <span data-payload-region="url" className={REGION}>
                {"  "}
                <span className={CODE.key}>&quot;slug&quot;</span>:{" "}
                <span className={CODE.string}>&quot;sale&quot;</span>,{"\n  "}
                <span className={CODE.key}>&quot;short_url&quot;</span>:{" "}
                <span className={CODE.string}>&quot;https://rii.link/sale&quot;</span>,
              </span>
              <span data-payload-region="expiry" className={REGION}>
                {"  "}
                <span className={CODE.key}>&quot;expires_at&quot;</span>:{" "}
                <span className={CODE.string}>&quot;2026-12-31T23:00:00Z&quot;</span>,
              </span>
              <span data-payload-region="contract" className={REGION}>
                {"  "}
                <span className={CODE.key}>&quot;updated_at&quot;</span>:{" "}
                <span className={CODE.string}>&quot;2026-09-12T08:30:00Z&quot;</span>
              </span>
              {"}"}
            </pre>
          </Terminal>
        </div>

        <div className="flex min-w-0 flex-col gap-6">
          {CALLOUTS.map((c) => (
            <div key={c.for} data-payload-callout data-for={c.for} className="relative md:pl-7">
              <span
                data-payload-connector
                className="absolute left-0 top-[13px] hidden h-[2px] w-5 origin-left bg-brand-600/60 md:block"
              />
              <div className="font-jetbrains text-[12.5px] font-semibold text-brand-600">
                {c.kicker}
              </div>
              <p className="mt-1 text-[14.5px] text-muted">{c.body}</p>
            </div>
          ))}
          <Button
            href={routePath("docs")}
            className="mt-2 self-start"
            iconRight={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4}>
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            }
          >
            Read the developer docs
          </Button>
        </div>
      </div>
    </SectionShell>
  );
}
